import { createSlice, PayloadAction, isAnyOf } from "@reduxjs/toolkit";

// --- Types and Interfaces ---

// Added "menu_order" to the type definition
export type OrderByOptions =
  | "menu_order"
  | "popularity"
  | "date"
  | "price-asc"
  | "price-desc";

export type AttributeFilterPayload = {
  [key: string]: string[];
};

export interface FilterState {
  category: number[];
  vehicle: number[];
  tag: number[];
  brand: string[];
  attributes: AttributeFilterPayload;
  minPrice: number | null;
  maxPrice: number | null;
  search: string;
  codeSearch: boolean;
  orderby: OrderByOptions;
  onSale: boolean;
  inStock: boolean;
  sku: string;
}

export interface PaginationState {
  currentPage: number;
  totalPages: number;
  totalProducts: number;
  perPage: number;
}

export interface UIState {
  activeView: "grid" | "list";
}

export interface ProductState {
  filters: FilterState;
  pagination: PaginationState;
  ui: UIState;
}

interface SetAttributeFilterPayload {
  attribute: string;
  term: string;
}

interface SetPriceRangePayload {
  minPrice: number | null;
  maxPrice: number | null;
}

// --- Default State ---

const defaultState: ProductState = {
  filters: {
    category: [],
    vehicle: [],
    tag: [],
    brand: [],
    attributes: {},
    minPrice: null,
    maxPrice: null,
    search: "",
    codeSearch: false,
    orderby: "menu_order", // UPDATED: Set default to menu_order
    onSale: false,
    inStock: false,
    sku: "",
  },
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalProducts: 0,
    perPage: 20,
  },
  ui: {
    activeView: "grid",
  },
};

/**
 * Creates the initial state for the product slice by reading parameters from the current URL.
 * Falls back to default values if parameters are not present.
 * @returns {ProductState} The initial state for the Redux store.
 */
const createInitialState = (): ProductState => {
  const params = new URLSearchParams(window.location.search);
  const filters: FilterState = { ...defaultState.filters, attributes: {} }; // Start with a clean attributes object
  const pagination: PaginationState = { ...defaultState.pagination };
  const ui: UIState = { ...defaultState.ui };

  // --- Parse Filters ---
  filters.search = params.get("productSearch") || "";
  filters.codeSearch = filters.search !== "" && params.get("productCodeSearch") === "true";
  filters.onSale = params.get("productOnSale") === "true";
  filters.inStock = params.get("productInStock") === "true";

  const orderbyParam = params.get("productOrderby");
  if (
    orderbyParam &&
    ["menu_order", "popularity", "date", "price-asc", "price-desc"].includes(
      orderbyParam,
    )
  ) {
    filters.orderby = orderbyParam as OrderByOptions;
  }
  const minPrice = params.get("productMinPrice");
  filters.minPrice = minPrice ? parseInt(minPrice, 10) : null;
  const maxPrice = params.get("productMaxPrice");
  filters.maxPrice = maxPrice ? parseInt(maxPrice, 10) : null;
  const category = params.get("productCategory");
  filters.category = category
    ? category.split(",").map(Number).filter(Boolean)
    : [];
  filters.vehicle = [
    ...new Set(
      (params.get("productVehicle") || "")
        .split(",")
        .map(Number)
        .filter((id) => Number.isSafeInteger(id) && id > 0),
    ),
  ];
  const tag = params.get("productTag");
  filters.tag = tag ? tag.split(",").map(Number).filter(Boolean) : [];
  const brand = params.get("productBrand");
  filters.brand = brand ? brand.split(",") : [];

  const attributesParam = params.get("productAttribute");
  if (attributesParam) {
    const attributePairs = attributesParam.split(",");
    attributePairs.forEach((pair) => {
      // Split by the first colon to robustly separate key from value
      const separatorIndex = pair.indexOf(":");
      if (separatorIndex > 0) {
        const cleanTaxonomy = pair.substring(0, separatorIndex);
        const termsString = pair.substring(separatorIndex + 1);
        if (cleanTaxonomy && termsString) {
          const fullTaxonomy = `pa_${cleanTaxonomy}`;
          // Split terms by "_"
          filters.attributes[fullTaxonomy] = termsString.split("_");
        }
      }
    });
  }

  // --- Parse Pagination & UI ---
  const page = params.get("productPage");
  pagination.currentPage = page ? parseInt(page, 10) : 1;
  const perPage = params.get("productPerPage");
  pagination.perPage = perPage ? parseInt(perPage, 10) : 20;
  const view = params.get("productView");
  ui.activeView = view === "list" ? "list" : "grid";

  return { filters, pagination, ui };
};

const dynamicInitialState = createInitialState();

// --- Slice Definition ---

export const productSlice = createSlice({
  name: "product",
  initialState: dynamicInitialState,
  reducers: {
    replaceVehicles: (state, action: PayloadAction<number[]>) => {
      state.filters.vehicle = action.payload;
    },
    // A single reducer for toggling values in number arrays
    toggleCategory: (state, action: PayloadAction<number>) => {
      const categoryId = action.payload;
      const index = state.filters.category.indexOf(categoryId);
      if (index >= 0) {
        state.filters.category.splice(index, 1);
      } else {
        state.filters.category.push(categoryId);
      }
    },
    // A single reducer for toggling values in number arrays
    toggleTag: (state, action: PayloadAction<number>) => {
      const tagId = action.payload;
      const index = state.filters.tag.indexOf(tagId);
      if (index >= 0) {
        state.filters.tag.splice(index, 1);
      } else {
        state.filters.tag.push(tagId);
      }
    },
    // A single reducer for toggling values in string arrays
    toggleBrand: (state, action: PayloadAction<string>) => {
      const brandSlug = action.payload;
      const index = state.filters.brand.indexOf(brandSlug);
      if (index >= 0) {
        state.filters.brand.splice(index, 1);
      } else {
        state.filters.brand.push(brandSlug);
      }
    },

    // Specific reducers for complex or unique logic
    setAttributeFilter: (
      state,
      action: PayloadAction<SetAttributeFilterPayload>,
    ) => {
      const { attribute, term } = action.payload;
      const currentTerms = state.filters.attributes[attribute] || [];
      const termIndex = currentTerms.indexOf(term);

      if (termIndex >= 0) {
        state.filters.attributes[attribute].splice(termIndex, 1);
        if (state.filters.attributes[attribute].length === 0) {
          delete state.filters.attributes[attribute];
        }
      } else {
        state.filters.attributes[attribute] = [...currentTerms, term];
      }
    },
    setPriceRange: (state, action: PayloadAction<SetPriceRangePayload>) => {
      state.filters.minPrice = action.payload.minPrice;
      state.filters.maxPrice = action.payload.maxPrice;
    },

    // Reducers for exclusive filters that reset others
    setSearch: (state, action: PayloadAction<string>) => {
      state.filters.search = action.payload;
      if (!action.payload) state.filters.codeSearch = false;
    },
    setCodeSearch: (state, action: PayloadAction<boolean>) => {
      state.filters.codeSearch = action.payload;
    },
    setSku: (state, action: PayloadAction<string>) => {
      state.filters.sku = action.payload;
    },

    // Simple value setters
    setSorting: (state, action: PayloadAction<OrderByOptions>) => {
      state.filters.orderby = action.payload;
    },
    setOnSale: (state, action: PayloadAction<boolean>) => {
      state.filters.onSale = action.payload;
    },
    setInStock: (state, action: PayloadAction<boolean>) => {
      state.filters.inStock = action.payload;
    },

    // Specific actions for category that aren't simple toggles
    clearCategories: (state) => {
      state.filters.category = [];
    },
    replaceCategories: (state, action: PayloadAction<number[]>) => {
      state.filters.category = action.payload;
    },

    // --- Pagination and UI Reducers ---
    setPagination: (state, action: PayloadAction<Partial<PaginationState>>) => {
      Object.assign(state.pagination, action.payload);
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.pagination.currentPage = action.payload;
    },
    setPerPage: (state, action: PayloadAction<number>) => {
      state.pagination.perPage = action.payload;
      state.pagination.currentPage = 1; // This side effect is fine here
    },
    setActiveView: (state, action: PayloadAction<"grid" | "list">) => {
      state.ui.activeView = action.payload;
    },
    resetFilters: (state) => {
      // --- Create a deep enough copy to ensure nested object 'attributes' is also reset.
      state.filters = {
        ...defaultState.filters,
        attributes: {}, // Explicitly create a new, empty object for attributes
      };
    },
  },

  // --- Centralized Side-Effect Handling ---
  extraReducers: (builder) => {
    // This matcher listens for ANY of the specified filter-changing actions.
    const filterActions = [
      replaceVehicles,
      toggleCategory,
      toggleTag,
      toggleBrand,
      setAttributeFilter,
      setPriceRange,
      setSearch,
      setCodeSearch,
      setSku,
      setSorting,
      setOnSale,
      setInStock,
      clearCategories,
      replaceCategories,
      resetFilters,
    ];

    builder.addMatcher(isAnyOf(...filterActions), (state, action) => {
      // When any filter action is dispatched, reset the current page.
      state.pagination.currentPage = 1;

      // Also reset the SKU unless the action was specifically setSku
      if (action.type !== setSku.type) {
        state.filters.sku = "";
      }
    });
  },
});

export const {
  replaceVehicles,
  toggleCategory,
  toggleTag,
  toggleBrand,
  setAttributeFilter,
  setPriceRange,
  setSearch,
  setCodeSearch,
  setSku,
  setSorting,
  setOnSale,
  setInStock,
  clearCategories,
  replaceCategories,
  setPagination,
  setPage,
  setPerPage,
  setActiveView,
  resetFilters,
} = productSlice.actions;

export default productSlice.reducer;
